import React, { useState, useEffect, useRef, useCallback } from 'react';
import { createPortal } from 'react-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { Check, Camera, Upload, X, Pencil, Mic, MicOff, Plus, Minus, ChevronRight, Flag, MapPin, Target } from 'lucide-react';
import { Button } from './ui/button';
import { PinDistance } from './gps/PinDistance';
import { ScoreReactionOverlay } from './score-reactions/ScoreReactionOverlay';
import { useScoreReactionSequence } from './score-reactions/useScoreReactionSequence';
import { actualHole, actualHolePar, scoreResult, RESULT_LABELS } from './score-reactions/scoreResults';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
if (!BACKEND_URL) throw new Error('REACT_APP_BACKEND_URL is required for scorecard GPS requests.');
const API = `${BACKEND_URL}/api`;

// ============ HOLE INFO CARD COMPONENT ============
// Swipeable card that appears before each hole showing hole info
const HoleInfoCard = ({ 
  holeNumber, 
  par, 
  yardage, 
  handicap, 
  courseName,
  courseId,
  onDismiss,
  isVisible 
}) => {
  const [dragOffset, setDragOffset] = useState(0);
  const [isDragging, setIsDragging] = useState(false);
  const [dragStart, setDragStart] = useState(0);
  const [isExiting, setIsExiting] = useState(false);
  const [isEntering, setIsEntering] = useState(true);
  const audioContextRef = useRef(null);

  // Entrance animation
  useEffect(() => {
    if (isVisible) {
      setIsEntering(true);
      const timer = setTimeout(() => setIsEntering(false), 300);
      return () => clearTimeout(timer);
    }
  }, [isVisible, holeNumber]);

  // Play swoosh sound
  const playSwoosh = useCallback(() => {
    try {
      const audioContext = audioContextRef.current || new (window.AudioContext || window.webkitAudioContext)();
      audioContextRef.current = audioContext;
      
      // Resume audio context if suspended (required for mobile browsers)
      if (audioContext.state === 'suspended') {
        audioContext.resume();
      }
      
      const oscillator = audioContext.createOscillator();
      const gainNode = audioContext.createGain();
      
      oscillator.connect(gainNode);
      gainNode.connect(audioContext.destination);
      
      oscillator.frequency.setValueAtTime(300, audioContext.currentTime);
      oscillator.frequency.exponentialRampToValueAtTime(80, audioContext.currentTime + 0.15);
      
      gainNode.gain.setValueAtTime(0.08, audioContext.currentTime);
      gainNode.gain.exponentialRampToValueAtTime(0.001, audioContext.currentTime + 0.15);
      
      oscillator.start(audioContext.currentTime);
      oscillator.stop(audioContext.currentTime + 0.15);
    } catch (e) {}
  }, []);

  // Touch handlers
  const handleTouchStart = (e) => {
    setIsDragging(true);
    setDragStart(e.touches[0].clientX);
    setDragOffset(0);
  };

  const handleTouchMove = (e) => {
    if (!isDragging) return;
    const offset = e.touches[0].clientX - dragStart;
    setDragOffset(offset);
  };

  const handleTouchEnd = () => {
    if (!isDragging) return;
    const threshold = 80;
    if (Math.abs(dragOffset) > threshold) {
      playSwoosh();
      setIsExiting(true);
      setTimeout(() => {
        onDismiss();
        setIsExiting(false);
        setDragOffset(0);
      }, 300);
    } else {
      setDragOffset(0);
    }
    setIsDragging(false);
  };

  // Mouse handlers
  const handleMouseDown = (e) => {
    setIsDragging(true);
    setDragStart(e.clientX);
    setDragOffset(0);
  };

  const handleMouseMove = (e) => {
    if (!isDragging) return;
    setDragOffset(e.clientX - dragStart);
  };

  const handleMouseUp = () => {
    if (!isDragging) return;
    const threshold = 80;
    if (Math.abs(dragOffset) > threshold) {
      playSwoosh();
      setIsExiting(true);
      setTimeout(() => {
        onDismiss();
        setIsExiting(false);
        setDragOffset(0);
      }, 300);
    } else {
      setDragOffset(0);
    }
    setIsDragging(false);
  };

  // Get par color
  const getParColor = () => {
    if (par === 3) return 'text-blue-400';
    if (par === 4) return 'text-green-400';
    if (par === 5) return 'text-amber-400';
    return 'text-white';
  };

  // Calculate exit transform
  const getTransform = () => {
    if (isExiting) {
      return dragOffset > 0 ? 'translateX(120%)' : 'translateX(-120%)';
    }
    return `translateX(${dragOffset}px)`;
  };

  if (!isVisible) return null;

  const cardContent = (
    <div 
      className="fixed inset-0 z-[9999] flex items-center justify-center bg-black/70 backdrop-blur-sm"
      data-testid="hole-info-card" data-hole={holeNumber} role="dialog" aria-label={`Hole ${holeNumber} information`}
      style={{ touchAction: 'pan-y' }}
    >
      <div
        className={`
          w-[90%] max-w-sm select-none
          transition-all duration-300 ease-out
          ${isDragging ? 'transition-none' : ''}
          ${isExiting ? 'opacity-0' : 'opacity-100'}
          ${isEntering ? 'scale-90 opacity-0' : 'scale-100 opacity-100'}
        `}
        style={{
          transform: getTransform(),
          cursor: isDragging ? 'grabbing' : 'grab'
        }}
        onTouchStart={handleTouchStart}
        onTouchMove={handleTouchMove}
        onTouchEnd={handleTouchEnd}
        onMouseDown={handleMouseDown}
        onMouseMove={handleMouseMove}
        onMouseUp={handleMouseUp}
        onMouseLeave={handleMouseUp}
      >
        {/* Card with green golf branding */}
        <div className="ps-fixed-contrast relative rounded-3xl max-h-[90dvh] overflow-y-auto shadow-2xl"
          style={{
            background: 'linear-gradient(135deg, #0d3320 0%, #166534 50%, #14532d 100%)',
            boxShadow: '0 0 60px rgba(34, 197, 94, 0.4), 0 25px 50px -12px rgba(0, 0, 0, 0.5)'
          }}
        >
          {/* Top decorative bar */}
          <div className="h-2 bg-gradient-to-r from-green-400 via-emerald-300 to-green-400" />
          
          {/* Flag icon decoration */}
          <div className="absolute top-6 right-6 opacity-20">
            <Flag className="w-20 h-20 text-green-300" />
          </div>

          <div className="p-6 relative">
            {/* Hole Number Badge */}
            <div className="flex items-center justify-center mb-4">
              <div className="relative">
                <div className="w-24 h-24 rounded-full bg-white/10 backdrop-blur flex items-center justify-center border-4 border-green-400/50">
                  <div className="text-center">
                    <div className="text-xs text-green-300 font-bold tracking-wider">HOLE</div>
                    <div className="text-5xl font-black text-white leading-none" data-testid="hole-info-number">{holeNumber}</div>
                  </div>
                </div>
                {/* Glow ring */}
                <div className="absolute inset-0 rounded-full animate-pulse" 
                  style={{ 
                    boxShadow: '0 0 30px rgba(34, 197, 94, 0.5)',
                    animation: 'pulse 2s ease-in-out infinite'
                  }} 
                />
              </div>
            </div>

            {/* Course name */}
            {courseName && (
              <p className="text-center text-green-300/80 text-sm mb-4 truncate px-4">{courseName}</p>
            )}

            {/* Hole Info Grid */}
            <div className="grid grid-cols-3 gap-3 mb-6">
              {/* Par */}
              <div className="bg-black/30 rounded-xl p-3 text-center">
                <Target className="w-5 h-5 mx-auto mb-1 text-green-400" />
                <div className="text-xs text-green-300/70 font-medium">PAR</div>
                <div className={`text-3xl font-black ${getParColor()}`} data-testid="hole-info-par">{par || '-'}</div>
              </div>
              
              {/* Yardage */}
              <div className="bg-black/30 rounded-xl p-3 text-center">
                <MapPin className="w-5 h-5 mx-auto mb-1 text-blue-400" />
                <div className="text-xs text-blue-300/70 font-medium">YARDS</div>
                <div className="text-3xl font-black text-blue-400">{yardage || '-'}</div>
              </div>
              
              {/* Handicap */}
              <div className="bg-black/30 rounded-xl p-3 text-center">
                <div className="w-5 h-5 mx-auto mb-1 text-amber-400 font-bold text-sm">HCP</div>
                <div className="text-xs text-amber-300/70 font-medium">RATING</div>
                <div className="text-3xl font-black text-amber-400">{handicap || '-'}</div>
              </div>
            </div>

            <PinDistance key={`${courseId}-${holeNumber}`} courseId={courseId} hole={holeNumber} />

            {/* Swipe instruction */}
            <div className="flex items-center justify-center gap-2 text-green-300/60 text-sm">
              <ChevronRight className="w-4 h-4 rotate-180" />
              <span>Swipe to enter score</span>
              <ChevronRight className="w-4 h-4" />
            </div>
          </div>

          {/* Bottom decorative bar */}
          <div className="h-1 bg-gradient-to-r from-transparent via-green-400/50 to-transparent" />
        </div>
      </div>
    </div>
  );
  
  // Use portal to render at body level so it overlays everything
  return createPortal(cardContent, document.body);
};

// Tally Counter Score Box - Large clicker style with roll selector
const ScoreBox = ({ value, onChange, disabled, holeNumber, par }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [tempScore, setTempScore] = useState(parseInt(value) || par || 4);
  const scrollRef = useRef(null);
  
  // Detect theme from document class (App.js applies theme via CSS classes)
  const [isDark, setIsDark] = useState(() => document.documentElement.classList.contains('theme-dark'));
  
  useEffect(() => {
    // Check document for theme class
    const checkTheme = () => {
      const isLightTheme = document.documentElement.classList.contains('theme-light');
      setIsDark(!isLightTheme);
    };
    
    checkTheme();
    
    // Watch for class changes on document
    const observer = new MutationObserver(checkTheme);
    observer.observe(document.documentElement, { attributes: true, attributeFilter: ['class'] });
    
    return () => observer.disconnect();
  }, []);
  
  // When opening, set temp score to current value or par
  useEffect(() => {
    if (isOpen) {
      setTempScore(parseInt(value) || par || 4);
      // Scroll to current value
      setTimeout(() => {
        if (scrollRef.current) {
          const targetScore = parseInt(value) || par || 4;
          const scrollPosition = (targetScore - 1) * 80; // 80px per item
          scrollRef.current.scrollTo({ top: scrollPosition, behavior: 'smooth' });
        }
      }, 100);
    }
  }, [isOpen, value, par]);
  
  const handleConfirm = () => {
    onChange(tempScore.toString());
    setIsOpen(false);
  };
  
  const handleIncrement = () => {
    setTempScore(prev => Math.min(prev + 1, 15));
  };
  
  const handleDecrement = () => {
    setTempScore(prev => Math.max(prev - 1, 1));
  };
  
  const handleScroll = (e) => {
    const scrollTop = e.target.scrollTop;
    const itemHeight = 80;
    const newScore = Math.round(scrollTop / itemHeight) + 1;
    if (newScore >= 1 && newScore <= 15 && newScore !== tempScore) {
      setTempScore(newScore);
    }
  };
  
  // Get score color based on par comparison
  const getScoreStyle = () => {
    if (!value) return 'bg-green-50 border-2 border-green-200 text-green-400';
    const scoreVal = parseInt(value);
    const parVal = par || 4;
    
    if (scoreVal < parVal) {
      return 'bg-green-200 border-2 border-green-500 text-green-800';
    } else if (scoreVal === parVal) {
      return 'bg-green-100 border-2 border-green-400 text-green-700';
    } else if (scoreVal === parVal + 1) {
      return 'bg-amber-100 border-2 border-amber-400 text-amber-700';
    } else {
      return 'bg-red-100 border-2 border-red-400 text-red-700';
    }
  };
  
  const getScoreLabel = (score, parVal) => {
    const result = scoreResult(score, parVal);
    if (result) return RESULT_LABELS[result];
    if (!parVal) return 'Par unavailable';
    const diff = score - parVal;
    if (diff < -3) return `${Math.abs(diff)} Under Par`;
    if (diff === -1) return 'Birdie';
    if (diff === 0) return 'Par';
    if (diff === 1) return 'Bogey';
    if (diff === 2) return 'Double';
    return `+${diff}`;
  };
  
  const getScoreLabelColor = (score, parVal) => {
    if (!parVal) return 'text-stone-300';
    const diff = score - parVal;
    if (diff <= -1) return 'text-green-500';
    if (diff === 0) return 'text-blue-500';
    if (diff === 1) return 'text-amber-500';
    return 'text-red-500';
  };
  
  return (
    <>
      {/* Score Display Box - Taps to open counter */}
      <button
        type="button"
        onClick={() => !disabled && setIsOpen(true)}
        disabled={disabled}
        className={`w-full h-9 text-base font-bold rounded-lg flex items-center justify-center transition-all ${getScoreStyle()} ${disabled ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer active:scale-95 hover:scale-105'}`}
        data-testid={`score-hole-${holeNumber}`}
      >
        {value || '-'}
      </button>
      
      {/* Tally Counter Modal - Using Portal to render at document root for proper centering */}
      {isOpen && typeof document !== 'undefined' && document.body && createPortal(
        <div 
          className="fixed inset-0 bg-black/70 flex items-center justify-center p-4"
          data-testid="score-picker" role="dialog" aria-label={`Enter score for hole ${holeNumber}`}
          style={{ zIndex: 9999 }}
          onClick={() => setIsOpen(false)}
        >
          <div 
            className={`rounded-3xl w-full max-w-xs p-6 shadow-2xl transform ${
              isDark 
                ? 'bg-gradient-to-b from-stone-800 to-stone-900' 
                : 'bg-gradient-to-b from-green-600 to-green-700'
            }`}
            onClick={(e) => e.stopPropagation()}
            style={{ animation: 'scaleIn 0.2s ease-out' }}
          >
            {/* Header */}
            <div className="text-center mb-4">
              <p className={`text-sm uppercase tracking-wider ${isDark ? 'text-stone-400' : 'text-green-200'}`}>Hole {holeNumber}</p>
              <p className="text-white text-lg font-semibold" data-testid="score-picker-par">Par {par || '—'}</p>
            </div>
            
            {/* Large Tally Counter */}
            <div className={`relative rounded-2xl p-4 mb-4 ${isDark ? 'bg-stone-950' : 'bg-green-800/50'}`}>
              {/* Decrement Button */}
              <button
                onClick={handleDecrement}
                data-testid="score-picker-decrement" aria-label="Decrease score"
                className={`absolute left-2 top-1/2 -translate-y-1/2 w-14 h-14 rounded-full active:scale-95 flex items-center justify-center transition-all ${
                  isDark ? 'bg-stone-700 hover:bg-stone-600' : 'bg-green-500 hover:bg-green-400'
                }`}
              >
                <Minus className="w-8 h-8 text-white" />
              </button>
              
              {/* Score Display - Large Counter */}
              <div className="text-center py-4">
                <div 
                  className="text-8xl font-black text-white tabular-nums"
                  data-testid="score-picker-value"
                  style={{ 
                    textShadow: '0 0 40px rgba(255,255,255,0.3)',
                    fontFamily: 'system-ui, -apple-system, sans-serif'
                  }}
                >
                  {tempScore}
                </div>
                <p data-testid="score-picker-result" className={`text-xl font-bold mt-2 ${getScoreLabelColor(tempScore, par)}`}>
                  {getScoreLabel(tempScore, par)}
                </p>
              </div>
              
              {/* Increment Button */}
              <button
                onClick={handleIncrement}
                data-testid="score-picker-increment" aria-label="Increase score"
                className={`absolute right-2 top-1/2 -translate-y-1/2 w-14 h-14 rounded-full active:scale-95 flex items-center justify-center transition-all ${
                  isDark ? 'bg-stone-700 hover:bg-stone-600' : 'bg-green-500 hover:bg-green-400'
                }`}
              >
                <Plus className="w-8 h-8 text-white" />
              </button>
            </div>
            
            {/* Quick Select Scroll Wheel */}
            <div 
              ref={scrollRef}
              onScroll={handleScroll}
              className="h-24 overflow-y-auto snap-y snap-mandatory scrollbar-hide mb-4 relative"
              style={{ scrollSnapType: 'y mandatory' }}
            >
              <div className="py-8">
                {[1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12, 13, 14, 15].map((score) => (
                  <div
                    key={score}
                    onClick={() => setTempScore(score)}
                    data-testid={`score-picker-select-${score}`} role="button" tabIndex={0}
                    onKeyDown={event => { if (event.key === 'Enter' || event.key === ' ') { event.preventDefault(); setTempScore(score); } }}
                    className={`h-20 flex items-center justify-center snap-center cursor-pointer transition-all ${
                      tempScore === score 
                        ? 'scale-110' 
                        : 'opacity-40 scale-90'
                    }`}
                    style={{ scrollSnapAlign: 'center' }}
                  >
                    <span className={`text-4xl font-bold ${
                      tempScore === score ? 'text-white' : (isDark ? 'text-stone-500' : 'text-green-300')
                    }`}>
                      {score}
                    </span>
                  </div>
                ))}
              </div>
              {/* Highlight indicator */}
              <div className="absolute inset-x-0 top-1/2 -translate-y-1/2 h-16 border-y-2 border-green-500/50 pointer-events-none" />
            </div>
            
            {/* Action Buttons */}
            <div className="flex gap-3">
              <button
                onClick={() => setIsOpen(false)}
                data-testid="score-picker-cancel"
                className={`flex-1 py-4 font-semibold rounded-xl active:scale-95 transition-all ${
                  isDark 
                    ? 'bg-stone-700 text-stone-300 hover:bg-stone-600' 
                    : 'bg-green-800 text-green-100 hover:bg-green-700'
                }`}
              >
                Cancel
              </button>
              <button
                onClick={handleConfirm}
                data-testid="score-picker-confirm"
                className="flex-1 py-4 bg-green-500 text-white font-bold rounded-xl hover:bg-green-400 active:scale-95 transition-all shadow-lg shadow-green-500/30"
              >
                <Check className="w-5 h-5 inline mr-2" />
                Save
              </button>
            </div>
          </div>
        </div>,
        document.body
      )}
      
      {/* Animation keyframes */}
      <style>{`
        @keyframes scaleIn {
          from { transform: scale(0.8); opacity: 0; }
          to { transform: scale(1); opacity: 1; }
        }
        .scrollbar-hide::-webkit-scrollbar {
          display: none;
        }
        .scrollbar-hide {
          -ms-overflow-style: none;
          scrollbar-width: none;
        }
      `}</style>
    </>
  );
};

/**
 * Unified Score Entry Form Component
 * Used in: Add Score page, Skins game, Match Play
 * 
 * This component handles hole-by-hole score entry with:
 * - Voice entry mode (AI-powered)
 * - Manual entry mode
 * - Scan scorecard mode (AI-powered)
 * - Auto-save drafts
 * - 9/18 hole toggle
 * 
 * Props:
 * - numberOfHoles: 9 or 18 (can be controlled externally)
 * - onNumberOfHolesChange: callback when holes changes
 * - initialScores: array of initial scores
 * - onScoresChange: callback when scores change (receives array)
 * - onSave: callback when finalize is clicked (receives { scores, total, front9, back9, holes })
 * - showHolesSelector: whether to show 9/18 hole toggle (default true)
 * - showScanOption: whether to show scan scorecard option (default true)
 * - showVoiceOption: whether to show voice entry option (default true)
 * - autoSave: whether to auto-save scores (default true)
 * - autoSaveEndpoint: API endpoint for auto-save (optional)
 * - autoSavePayload: function that returns payload for auto-save (receives scores array)
 * - playerName: name to display (e.g., "John Doe")
 * - submitButtonText: text for submit button (default "Finalize Score")
 * - onCancel: callback for cancel button (optional)
 * - showTotalDisplay: whether to show total summary (default true)
 * - compact: compact mode for embedded use (default false)
 * - disabled: disable all inputs (default false)
 * - eventHoles: fixed hole count from event (overrides numberOfHoles if set)
 */
const ScoreEntryForm = ({
  numberOfHoles: externalHoles,
  onNumberOfHolesChange,
  initialScores = [],
  onScoresChange,
  onSave,
  showHolesSelector = true,
  showScanOption = true,
  showVoiceOption = true,
  autoSave = true,
  autoSaveEndpoint,
  autoSavePayload,
  playerName,
  submitButtonText = "Finalize Score",
  onCancel,
  showTotalDisplay = true,
  compact = false,
  disabled = false,
  saveDisabled = false, // Separate flag for disabling save button only
  eventHoles, // Fixed holes from event (e.g., skins game with 9 holes)
  startingNine = 'front', // Which nine to start with: 'front' or 'back'
  getActualHoleNumber, // Function to convert internal index to actual hole number
  holePars = [], // Array of par values for each hole (index 0 = hole 1, etc.)
  holeYardages = [], // Array of yardage values for each hole (index 0 = hole 1, etc.)
  holeHandicaps = [], // Array of handicap ratings for each hole
  showYardages = true, // Whether to display yardages
  darkMode = false, // Dark theme mode for scorecard
  courseName = '', // Name of the course for hole info cards
  courseId,
  showHoleInfoCards = true, // Whether to show swipeable hole info cards
  scoreReactions, // Optional personal/manual entry plug-in; omitted by group/competition cards.
  isMultiPlayer = false, // Whether this is a multi-player game
  allPlayersHoleScores = null, // Object mapping hole number to array of player scores (for multi-player games)
  onAllPlayersCompletedHole = null // Callback when checking if all players completed a hole
}) => {
  // Determine active holes - eventHoles takes priority
  const [internalHoles, setInternalHoles] = useState(eventHoles || externalHoles || 18);
  const holes = eventHoles || (externalHoles !== undefined ? externalHoles : internalHoles);
  
  // Score entry state - always maintain 18 slots
  const [scores, setScores] = useState(() => {
    if (initialScores.length > 0) {
      const mapped = initialScores.map(s => s === null ? '' : String(s));
      // Pad to 18 if needed
      while (mapped.length < 18) mapped.push('');
      return mapped;
    }
    return Array(18).fill('');
  });
  
  // Hole Info Card state - tracks which hole's info card is currently showing
  const [activeHoleInfoCard, setActiveHoleInfoCard] = useState(() => {
    // Start with hole 1 (or hole 10 if back 9)
    return startingNine === 'back' && holes === 9 ? 10 : 1;
  });
  const [holeInfoDismissed, setHoleInfoDismissed] = useState(new Set()); // Tracks dismissed cards
  
  // Entry mode: 'voice', 'scan', or 'manual'
  const [entryMode, setEntryMode] = useState('manual');
  
  // Auto-save state
  const [saving, setSaving] = useState(false);
  const [lastSaved, setLastSaved] = useState(null);
  const [saveError, setSaveError] = useState('');
  const holeTransitionTimer = useRef(null);
  useEffect(() => () => clearTimeout(holeTransitionTimer.current), []);
  const reactionSequence = useScoreReactionSequence({
    scope: `${scoreReactions?.context || ''}|${courseId || courseName}|${holes}|${startingNine}`,
    active: Boolean(scoreReactions) && !isMultiPlayer && entryMode === 'manual' && !disabled,
    enabled: Boolean(scoreReactions?.enabled),
    onAdvance: next => {
      const index = next - (holes === 9 && startingNine === 'back' ? 10 : 1);
      if (showHoleInfoCards && !holeInfoDismissed.has(next) && !scores[index]) setActiveHoleInfoCard(next);
    },
  });
  
  // Scan state
  const [scanPreview, setScanPreview] = useState(null);
  const [scanning, setScanning] = useState(false);
  const [scanResult, setScanResult] = useState(null);
  const [detectedPlayers, setDetectedPlayers] = useState([]); // Multi-player support
  const [selectedPlayerIndex, setSelectedPlayerIndex] = useState(null); // Which player's scores to use
  
  // Voice state
  const [isRecording, setIsRecording] = useState(false);
  const [voiceProcessing, setVoiceProcessing] = useState(false);
  const [voiceTranscript, setVoiceTranscript] = useState('');
  // Initialize currentVoiceHole based on startingNine - Back 9 starts at hole 10
  const [currentVoiceHole, setCurrentVoiceHole] = useState(() => 
    startingNine === 'back' && holes === 9 ? 10 : 1
  );
  const mediaRecorderRef = useRef(null);
  const audioChunksRef = useRef([]);

  // Update currentVoiceHole when startingNine or holes changes
  useEffect(() => {
    const startHole = startingNine === 'back' && holes === 9 ? 10 : 1;
    setCurrentVoiceHole(startHole);
  }, [startingNine, holes]);

  // Update scores when initialScores changes (but don't overwrite user-entered scores with empty)
  useEffect(() => {
    if (initialScores.length > 0) {
      // Check if new scores have any non-empty values
      const hasNewScores = initialScores.some(s => s !== null && s !== '' && s !== undefined);
      if (hasNewScores) {
        const mapped = initialScores.map(s => s === null ? '' : String(s));
        while (mapped.length < 18) mapped.push('');
        setScores(mapped);
      }
    }
  }, [JSON.stringify(initialScores)]);

  // Handle holes change (only if not controlled by eventHoles)
  const handleHolesChange = (newHoles) => {
    if (eventHoles) return; // Don't allow change if event has fixed holes
    
    if (externalHoles === undefined) {
      setInternalHoles(newHoles);
    }
    if (onNumberOfHolesChange) {
      onNumberOfHolesChange(newHoles);
    }
  };

  // Calculate totals
  const calculateTotals = () => {
    const front9 = scores.slice(0, 9).reduce((sum, s) => sum + (s ? parseInt(s) : 0), 0);
    const back9 = holes === 18 
      ? scores.slice(9, 18).reduce((sum, s) => sum + (s ? parseInt(s) : 0), 0)
      : 0;
    const relevantScores = scores.slice(0, holes);
    const filledCount = relevantScores.filter(s => s !== '' && s !== null).length;
    const allFilled = filledCount === holes;
    const total = front9 + (holes === 18 ? back9 : 0);
    
    return { front9, back9, total, filledCount, allFilled };
  };

  const { front9, back9, total, filledCount, allFilled } = calculateTotals();

  // Handle score change with immediate auto-save
  const handleScoreChange = async (index, value) => {
    if (reactionSequence.reaction) return;
    const sanitizedValue = value.replace(/[^0-9]/g, '');
    const newScores = [...scores];
    newScores[index] = sanitizedValue;
    setScores(newScores);
    
    // Notify parent
    if (onScoresChange) {
      onScoresChange(newScores);
    }
    
    // After score is entered, show the next hole's info card
    // For multi-player games, only show when ALL players have scores for this hole
    if (scoreReactions && !isMultiPlayer && entryMode === 'manual') {
      clearTimeout(holeTransitionTimer.current);
      setActiveHoleInfoCard(null);
      const hole = actualHole(index, holes, startingNine);
      reactionSequence.confirmed({ index, previous: scores[index], value: sanitizedValue,
        par: actualHolePar(holePars, index, holes, startingNine), hole,
        nextHole: showHoleInfoCards && index + 1 < holes ? hole + 1 : null });
    } else if (showHoleInfoCards && sanitizedValue !== '' && entryMode === 'manual') {
      const actualHoleEntered = (startingNine === 'back' && holes === 9) ? index + 10 : index + 1;
      const nextHole = actualHoleEntered + 1;
      const maxHole = (startingNine === 'back' && holes === 9) ? 18 : holes;
      
      // Check if we should show the next hole's card
      let shouldShowNextHoleCard = true;
      
      // For multi-player games, check if all players have completed this hole
      if (isMultiPlayer && allPlayersHoleScores) {
        // allPlayersHoleScores is an object: { holeNumber: [score1, score2, ...] }
        const holeScores = allPlayersHoleScores[actualHoleEntered] || [];
        // Count non-null scores (this player's score will be updated on parent re-render)
        const completedCount = holeScores.filter(s => s !== null && s !== undefined).length;
        const totalPlayers = Object.keys(allPlayersHoleScores).length > 0 
          ? (allPlayersHoleScores.totalPlayers || holeScores.length)
          : 1;
        
        // Don't show card until all players have scores for this hole
        // The parent will pass updated allPlayersHoleScores after save
        shouldShowNextHoleCard = false; // Let parent control this via callback
      }
      
      // Show next hole's card after a short delay (so user sees their score saved)
      if (shouldShowNextHoleCard && nextHole <= maxHole && !holeInfoDismissed.has(nextHole)) {
        clearTimeout(holeTransitionTimer.current);
        holeTransitionTimer.current = setTimeout(() => {
          setActiveHoleInfoCard(nextHole);
        }, 500);
      }
    }
    
    // Auto-save immediately if enabled and value is not empty
    if (autoSave && autoSaveEndpoint && sanitizedValue !== '') {
      setSaveError(''); setLastSaved(null);
      setSaving(true);
      try {
        const payload = autoSavePayload 
          ? autoSavePayload(newScores) 
          : { hole_scores: newScores.map(s => s === '' ? null : parseInt(s) || null) };
        await axios.put(autoSaveEndpoint, payload);
        setLastSaved(new Date());
        
        // For multi-player, notify parent that this player's score was saved
        // Parent can then check if all players completed and trigger hole info card
        if (isMultiPlayer && onAllPlayersCompletedHole) {
          const actualHoleEntered = (startingNine === 'back' && holes === 9) ? index + 10 : index + 1;
          onAllPlayersCompletedHole(actualHoleEntered);
        }
      } catch (error) {
        console.error("Auto-save failed:", error);
        if (scoreReactions) {
          reactionSequence.cancel();
          setActiveHoleInfoCard(null);
          setSaveError(`Hole ${actualHole(index, holes, startingNine)} is on your card but could not be saved. Check your connection and save again.`);
          toast.error('Score could not be saved. Please try again.');
        }
      } finally {
        setSaving(false);
      }
    } else if (autoSave && sanitizedValue !== '') {
      // Just show saved indicator without API call (for local state)
      setLastSaved(new Date());
    }
  };
  
  // Handle hole info card dismissal
  const handleHoleInfoDismiss = () => {
    setHoleInfoDismissed(prev => new Set([...prev, activeHoleInfoCard]));
    setActiveHoleInfoCard(null);
  };
  
  // Show hole info card for a specific hole (allows reviewing after dismissal)
  const showHoleInfo = (holeNumber) => {
    // Remove from dismissed set so it can show
    setHoleInfoDismissed(prev => {
      const newSet = new Set(prev);
      newSet.delete(holeNumber);
      return newSet;
    });
    setActiveHoleInfoCard(holeNumber);
  };
  
  // Check if current hole's info card should be shown
  const shouldShowHoleInfoCard = () => {
    if (reactionSequence.reaction) return false;
    if (!showHoleInfoCards) return false;
    if (entryMode !== 'manual') return false;
    if (!activeHoleInfoCard) return false;
    if (holeInfoDismissed.has(activeHoleInfoCard)) return false;
    
    // Check if this hole already has a score
    const holeIndex = (startingNine === 'back' && holes === 9) 
      ? activeHoleInfoCard - 10 
      : activeHoleInfoCard - 1;
    if (holeIndex >= 0 && scores[holeIndex] && scores[holeIndex] !== '') return false;
    
    return true;
  };

  // Handle scorecard scan
  const handleScanFile = async (file) => {
    if (!file) return;
    setScanPreview(URL.createObjectURL(file));
    setScanning(true);
    setScanResult(null);
    setDetectedPlayers([]);
    setSelectedPlayerIndex(null);

    try {
      const formData = new FormData();
      formData.append('file', file);
      
      const response = await axios.post(`${API}/scorecard/analyze`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });

      if (response.data.success) {
        setScanResult(response.data);
        
        // Check if multiple players detected
        const players = response.data.players || [];
        const playerCount = response.data.player_count || 1;
        
        if (playerCount > 1 && players.length > 1) {
          // Multiple players found - show selection UI
          setDetectedPlayers(players);
          toast.info(`Found ${playerCount} players on scorecard. Select which scores to use.`);
        } else {
          // Single player - apply scores directly
          const scannedScores = response.data.holes.slice(0, holes).map(h => 
            h.score !== null && h.score !== undefined ? String(h.score) : ''
          );
          // Pad to 18
          while (scannedScores.length < 18) scannedScores.push('');
          setScores(scannedScores);
          if (onScoresChange) onScoresChange(scannedScores);
          
          // Auto-save scanned scores immediately
          if (autoSave && autoSaveEndpoint) {
            try {
              const payload = autoSavePayload 
                ? autoSavePayload(scannedScores) 
                : { hole_scores: scannedScores.map(s => s === '' ? null : parseInt(s) || null) };
              await axios.put(autoSaveEndpoint, payload);
              setLastSaved(new Date());
              toast.success("Scorecard scanned and auto-saved! Review and finalize.");
            } catch (error) {
              console.error("Auto-save after scan failed:", error);
              toast.success("Scorecard scanned! Review and finalize.");
            }
          } else {
            toast.success("Scorecard scanned! Review and finalize.");
          }
        }
      } else {
        toast.error(response.data.message || "Could not analyze scorecard");
        setScanResult({ success: false, message: response.data.message });
      }
    } catch (error) {
      console.error("Scan error:", error);
      toast.error("Failed to scan scorecard");
      setScanResult({ success: false, message: "Failed to scan" });
    } finally {
      setScanning(false);
    }
  };

  // Apply selected player's scores
  const applyPlayerScores = async (playerIndex) => {
    if (playerIndex < 0 || playerIndex >= detectedPlayers.length) return;
    
    const player = detectedPlayers[playerIndex];
    const playerScores = (player.hole_scores || []).slice(0, holes).map(s => 
      s !== null && s !== undefined ? String(s) : ''
    );
    // Pad to 18
    while (playerScores.length < 18) playerScores.push('');
    
    setScores(playerScores);
    setSelectedPlayerIndex(playerIndex);
    if (onScoresChange) onScoresChange(playerScores);
    
    // Clear player selection UI after applying
    setDetectedPlayers([]);
    
    // Auto-save
    if (autoSave && autoSaveEndpoint) {
      try {
        const payload = autoSavePayload 
          ? autoSavePayload(playerScores) 
          : { hole_scores: playerScores.map(s => s === '' ? null : parseInt(s) || null) };
        await axios.put(autoSaveEndpoint, payload);
        setLastSaved(new Date());
        const playerName = player.player_name || `Player ${playerIndex + 1}`;
        toast.success(`Applied ${playerName}'s scores and auto-saved!`);
      } catch (error) {
        console.error("Auto-save after player selection failed:", error);
        toast.success("Scores applied! Review and finalize.");
      }
    } else {
      toast.success("Scores applied! Review and finalize.");
    }
  };

  const clearScan = () => {
    setScanPreview(null);
    setScanResult(null);
    setDetectedPlayers([]);
    setSelectedPlayerIndex(null);
    setScores(Array(18).fill(''));
    if (onScoresChange) onScoresChange(Array(18).fill(''));
  };

  // Voice Recording Functions
  const startRecording = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ 
        audio: { echoCancellation: false, noiseSuppression: false, autoGainControl: true }
      });
      
      let mimeType = 'audio/webm';
      if (MediaRecorder.isTypeSupported('audio/mp4')) mimeType = 'audio/mp4';
      else if (MediaRecorder.isTypeSupported('audio/webm;codecs=opus')) mimeType = 'audio/webm;codecs=opus';
      
      const mediaRecorder = new MediaRecorder(stream, { mimeType });
      mediaRecorderRef.current = mediaRecorder;
      audioChunksRef.current = [];

      mediaRecorder.ondataavailable = (event) => {
        if (event.data.size > 0) audioChunksRef.current.push(event.data);
      };

      mediaRecorder.onstop = async () => {
        stream.getTracks().forEach(track => track.stop());
        const audioBlob = new Blob(audioChunksRef.current, { type: mimeType });
        await processVoiceRecording(audioBlob);
      };

      mediaRecorder.start();
      setIsRecording(true);
    } catch (error) {
      console.error("Microphone error:", error);
      toast.error("Could not access microphone");
    }
  };

  const stopRecording = () => {
    if (mediaRecorderRef.current && mediaRecorderRef.current.state === 'recording') {
      mediaRecorderRef.current.stop();
      setIsRecording(false);
    }
  };

  const processVoiceRecording = async (audioBlob) => {
    setVoiceProcessing(true);
    setVoiceTranscript('Processing...');
    
    try {
      // Determine file extension based on blob type (same as AddScorePage)
      let fileExt = 'webm';
      if (audioBlob.type.includes('mp4')) fileExt = 'mp4';
      else if (audioBlob.type.includes('ogg')) fileExt = 'ogg';
      
      const formData = new FormData();
      // Use 'file' key to match backend endpoint (File(...) parameter)
      formData.append('file', audioBlob, `voice.${fileExt}`);
      formData.append('current_hole', String(currentVoiceHole));
      formData.append('current_hole_par', '4'); // Default par - can be enhanced to use actual course pars
      
      const response = await axios.post(`${API}/voice/transcribe`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });

      if (response.data.success) {
        setVoiceTranscript(response.data.transcript);
        
        // Handle parsed_scores array (matches AddScorePage pattern)
        if (response.data.parsed_scores && response.data.parsed_scores.length > 0) {
          const newScores = [...scores];
          const scoresEntered = [];
          
          for (const { hole, score } of response.data.parsed_scores) {
            // Use currentVoiceHole if hole is null
            const targetHole = hole !== null ? hole : currentVoiceHole;
            
            // Convert spoken hole number to array index based on startingNine
            // For 9-hole Back 9: hole 10 -> index 0, hole 11 -> index 1, etc.
            // For 9-hole Front 9 or 18-hole: hole 1 -> index 0, hole 10 -> index 9
            let arrayIndex;
            if (holes === 9 && startingNine === 'back') {
              // Back 9 only: holes 10-18 map to indices 0-8
              if (targetHole >= 10 && targetHole <= 18) {
                arrayIndex = targetHole - 10;
              } else {
                // User said hole 1-9 in Back 9 mode - invalid, skip
                toast.warning(`Playing Back 9 (holes 10-18). You said hole ${targetHole}`);
                continue;
              }
            } else {
              // Front 9 or 18-hole: standard mapping
              arrayIndex = targetHole - 1;
            }
            
            if (arrayIndex >= 0 && arrayIndex < holes && score >= 1 && score <= 15) {
              newScores[arrayIndex] = String(score);
              scoresEntered.push({ hole: targetHole, score });
              
              // Move to next hole (using actual hole number for display)
              const nextDisplayHole = holes === 9 && startingNine === 'back' 
                ? Math.min(targetHole + 1, 18)
                : Math.min(targetHole + 1, holes);
              setCurrentVoiceHole(nextDisplayHole);
            }
          }
          
          setScores(newScores);
          if (onScoresChange) onScoresChange(newScores);
          
          // Auto-save
          if (autoSave && autoSaveEndpoint) {
            try {
              const payload = autoSavePayload 
                ? autoSavePayload(newScores) 
                : { hole_scores: newScores.map(s => s === '' ? null : parseInt(s) || null) };
              await axios.put(autoSaveEndpoint, payload);
              setLastSaved(new Date());
            } catch (e) { console.error("Auto-save failed:", e); }
          }
          
          // Show success message
          const displayScores = scoresEntered.map(s => `Hole ${s.hole}: ${s.score}`).join(', ');
          toast.success(`Recorded: ${displayScores}`);
        } else if (response.data.message) {
          toast.warning(response.data.message);
        } else {
          toast.warning("Could not understand. Try saying: 'Hole 3, I got a 5'");
        }
      } else {
        toast.error(response.data.message || "Could not process voice input");
      }
    } catch (error) {
      console.error("Voice processing error:", error);
      toast.error("Voice processing failed");
    } finally {
      setVoiceProcessing(false);
    }
  };

  // Handle finalize/submit
  const handleSubmit = () => {
    if (!allFilled) {
      toast.error(`Please enter all ${holes} hole scores`);
      return;
    }
    
    if (onSave) {
      onSave({
        scores: scores.slice(0, holes).map(s => parseInt(s)),
        total,
        front9,
        back9: holes === 18 ? back9 : null,
        holes
      });
    }
  };

  // Get score color based on value (golf scoring colors)
  const getScoreColor = (score) => {
    if (!score) return 'border-stone-300';
    const val = parseInt(score);
    if (val <= 3) return 'bg-green-50 border-green-400 text-green-700';
    if (val === 4) return 'bg-blue-50 border-blue-400 text-blue-700';
    if (val === 5) return 'bg-yellow-50 border-yellow-400 text-yellow-700';
    return 'bg-red-50 border-red-400 text-red-700';
  };

  return (
    <div className={`space-y-3 ${compact ? '' : ''} relative`} data-testid="score-entry-form">
      {saveError && <p role="alert" className="text-sm text-red-500" data-testid="score-draft-save-error">{saveError}</p>}
      {reactionSequence.reaction && <ScoreReactionOverlay key={reactionSequence.reaction.id}
        result={reactionSequence.reaction.result} hole={reactionSequence.reaction.hole}
        onComplete={() => reactionSequence.complete(reactionSequence.reaction.id)} />}
      {/* Hole Info Card Overlay */}
      {shouldShowHoleInfoCard() && (
        <HoleInfoCard
          holeNumber={activeHoleInfoCard}
          par={holePars[holePars.length === 9 && startingNine === 'back' ? activeHoleInfoCard - 10 : activeHoleInfoCard - 1] || null}
          yardage={holeYardages[holeYardages.length === 9 && startingNine === 'back' ? activeHoleInfoCard - 10 : activeHoleInfoCard - 1] || null}
          handicap={holeHandicaps[holeHandicaps.length === 9 && startingNine === 'back' ? activeHoleInfoCard - 10 : activeHoleInfoCard - 1] || null}
          courseName={courseName}
          courseId={courseId}
          onDismiss={handleHoleInfoDismiss}
          isVisible={true}
        />
      )}
      
      {/* Entry Mode Toggle (Voice / Manual) - Simple text toggle style */}
      {(showVoiceOption || showScanOption) && (
        <div className="flex items-center justify-center gap-6 mb-3">
          {showVoiceOption && (
            <>
              <button
                onClick={() => setEntryMode('voice')}
                disabled={disabled}
                className={`text-lg font-bold transition-all ${
                  entryMode === 'voice' 
                    ? darkMode ? 'text-amber-400 scale-105' : 'text-[var(--ps-primary,#22c55e)] scale-105'
                    : darkMode ? 'text-gray-500 hover:text-gray-400' : 'text-stone-400 hover:text-stone-500'
                }`}
                data-testid="voice-mode-btn"
              >
                Voice
              </button>
              <span className={darkMode ? 'text-gray-600' : 'text-stone-300'}>|</span>
            </>
          )}
          <button
            onClick={() => setEntryMode('manual')}
            disabled={disabled}
            className={`text-lg font-bold transition-all ${
              entryMode === 'manual' 
                ? darkMode ? 'text-amber-400 scale-105' : 'text-[var(--ps-primary,#22c55e)] scale-105'
                : darkMode ? 'text-gray-500 hover:text-gray-400' : 'text-stone-400 hover:text-stone-500'
            }`}
            data-testid="manual-mode-btn"
          >
            Manual
          </button>
        </div>
      )}

      {/* Player Name & Save Status Header */}
      {(playerName || lastSaved || saving) && (
        <div className="flex items-center justify-between">
          {playerName && (
            <p className={`text-base font-bold ${darkMode ? 'text-white' : 'text-stone-700'}`}>
              {playerName}
            </p>
          )}
          {/* Auto-save status indicator */}
          <div className="flex items-center gap-2">
            {lastSaved && !saving && (
              <span data-testid="score-draft-saved" className={`text-sm flex items-center gap-1 font-semibold ${darkMode ? 'text-green-400' : 'text-green-600'}`}>
                <Check className="w-4 h-4" />
                Saved
              </span>
            )}
            {saving && (
              <span data-testid="score-draft-saving" className={`text-sm flex items-center gap-1 font-semibold ${darkMode ? 'text-amber-400' : 'text-amber-600'}`}>
                <div className={`w-4 h-4 border-2 ${darkMode ? 'border-amber-400' : 'border-amber-600'} border-t-transparent rounded-full animate-spin`} />
                Saving...
              </span>
            )}
          </div>
        </div>
      )}

      {/* Number of Holes Selector (only in manual mode and if allowed) */}
      {showHolesSelector && entryMode === 'manual' && !eventHoles && (
        <div className="flex items-center justify-center gap-3">
          <span className="text-base font-semibold text-stone-600">Playing:</span>
          <div className="flex bg-stone-100 rounded-xl p-1">
            <button
              onClick={() => handleHolesChange(9)}
              disabled={disabled}
              className={`px-5 py-2 rounded-lg text-base font-bold transition-colors ${
                holes === 9 ? 'bg-amber-500 text-white shadow-sm' : 'text-stone-600 hover:text-stone-800'
              }`}
              data-testid="9-holes-toggle"
            >
              9 Holes
            </button>
            <button
              onClick={() => handleHolesChange(18)}
              disabled={disabled}
              className={`px-5 py-2 rounded-lg text-base font-bold transition-colors ${
                holes === 18 ? 'bg-amber-500 text-white shadow-sm' : 'text-stone-600 hover:text-stone-800'
              }`}
              data-testid="18-holes-toggle"
            >
              18 Holes
            </button>
          </div>
        </div>
      )}

      {/* Scan Mode UI */}
      {entryMode === 'scan' && (
        <div className="space-y-3">
          {!scanPreview ? (
            <div className="space-y-3">
              {/* Take Photo Button */}
              <label className="block border-2 border-dashed border-blue-300 rounded-lg p-4 text-center cursor-pointer hover:border-blue-400 hover:bg-blue-50 transition-colors bg-blue-50/50">
                <input
                  type="file"
                  accept="image/*"
                  capture="environment"
                  className="hidden"
                  onChange={(e) => e.target.files?.[0] && handleScanFile(e.target.files[0])}
                  disabled={disabled}
                  data-testid="camera-input"
                />
                <Camera className="w-6 h-6 mx-auto mb-1 text-blue-500" />
                <p className="text-sm font-medium text-blue-700">Take Photo</p>
              </label>
              {/* Upload from Gallery Button */}
              <label className="block border-2 border-dashed border-stone-300 rounded-lg p-4 text-center cursor-pointer hover:border-stone-400 hover:bg-stone-50 transition-colors">
                <input
                  type="file"
                  accept="image/*"
                  className="hidden"
                  onChange={(e) => e.target.files?.[0] && handleScanFile(e.target.files[0])}
                  disabled={disabled}
                  data-testid="gallery-input"
                />
                <Upload className="w-6 h-6 mx-auto mb-1 text-stone-500" />
                <p className="text-sm font-medium text-stone-600">Upload from Gallery</p>
              </label>
            </div>
          ) : (
            <div className="space-y-3">
              <div className="relative">
                <img src={scanPreview} alt="Scorecard" className="w-full rounded-lg border" />
                {scanning && (
                  <div className="absolute inset-0 bg-black/50 rounded-lg flex items-center justify-center">
                    <div className="text-center text-white">
                      <div className="w-8 h-8 border-2 border-white border-t-transparent rounded-full animate-spin mx-auto mb-2" />
                      <p className="text-sm">Analyzing...</p>
                    </div>
                  </div>
                )}
              </div>
              {scanResult && !scanResult.success && (
                <div className="bg-red-50 border border-red-200 rounded-lg p-3 text-sm text-red-700">
                  {scanResult.message || "Could not read scorecard. Try manual entry."}
                </div>
              )}
              
              {/* Multi-Player Selection UI */}
              {detectedPlayers.length > 1 && (
                <div className="bg-amber-50 border border-amber-200 rounded-lg p-4 space-y-3" data-testid="player-selection">
                  <p className="text-sm font-medium text-amber-800">
                    Found {detectedPlayers.length} players on scorecard. Select which scores to apply:
                  </p>
                  <div className="space-y-2">
                    {detectedPlayers.map((player, idx) => {
                      const playerName = player.player_name || `Player ${idx + 1} (Name unclear)`;
                      const nameConfidence = player.name_confidence || 'low';
                      const scores = player.hole_scores || [];
                      const displayScores = scores.slice(0, 9).map(s => s ?? '-').join(', ');
                      
                      return (
                        <button
                          key={`scan-player-${playerName || idx}`}
                          onClick={() => applyPlayerScores(idx)}
                          className={`w-full text-left p-3 rounded-lg border-2 transition-colors ${
                            selectedPlayerIndex === idx 
                              ? 'border-amber-500 bg-amber-100' 
                              : 'border-stone-200 bg-white hover:border-amber-300 hover:bg-amber-50'
                          }`}
                          data-testid={`select-player-${idx}`}
                        >
                          <div className="flex items-center justify-between">
                            <div>
                              <span className="font-medium text-stone-800">{playerName}</span>
                              {nameConfidence === 'low' && (
                                <span className="ml-2 text-xs px-1.5 py-0.5 bg-yellow-100 text-yellow-700 rounded">
                                  Name uncertain
                                </span>
                              )}
                            </div>
                            <span className="text-sm font-mono text-stone-500">
                              Total: {player.total_score ?? '--'}
                            </span>
                          </div>
                          <p className="text-xs text-stone-500 mt-1 font-mono">
                            Front 9: {displayScores}...
                          </p>
                        </button>
                      );
                    })}
                  </div>
                </div>
              )}
              
              <Button
                variant="outline"
                size="sm"
                onClick={clearScan}
                className="w-full"
                disabled={disabled}
                data-testid="clear-scan-btn"
              >
                <X className="w-4 h-4 mr-1" /> Clear & Try Again
              </Button>
            </div>
          )}
        </div>
      )}

      {/* Voice Mode UI */}
      {entryMode === 'voice' && (
        <div className="space-y-4">
          {/* Current Hole Indicator */}
          <div className="text-center py-4 bg-stone-50 rounded-xl">
            <p className="text-sm text-stone-500 mb-1">Next Hole</p>
            <p className="text-4xl font-bold text-green-700">{currentVoiceHole}</p>
            <p className="text-xs text-stone-400 mt-1">
              {startingNine === 'back' && holes === 9 
                ? `${currentVoiceHole - 9} of ${holes}` 
                : `${currentVoiceHole} of ${holes}`}
              {startingNine === 'back' && holes === 9 && (
                <span className="text-amber-600 ml-1">(Back 9 first)</span>
              )}
            </p>
          </div>

          {/* Record Button */}
          <div className="flex justify-center">
            <button
              onClick={isRecording ? stopRecording : startRecording}
              disabled={voiceProcessing || disabled}
              className={`w-24 h-24 rounded-full flex flex-col items-center justify-center transition-all ${
                isRecording 
                  ? 'bg-red-500 text-white animate-pulse scale-110' 
                  : voiceProcessing
                    ? 'bg-stone-300 text-stone-500'
                    : 'bg-green-700 text-white hover:bg-green-800 hover:scale-105'
              }`}
              data-testid="voice-record-btn"
            >
              {voiceProcessing ? (
                <>
                  <div className="w-8 h-8 border-3 border-white border-t-transparent rounded-full animate-spin" />
                  <span className="text-xs mt-1">Processing</span>
                </>
              ) : isRecording ? (
                <>
                  <MicOff className="w-10 h-10" />
                  <span className="text-xs mt-1">Stop</span>
                </>
              ) : (
                <>
                  <Mic className="w-10 h-10" />
                  <span className="text-xs mt-1">Tap to speak</span>
                </>
              )}
            </button>
          </div>

          {/* Transcript Display */}
          {voiceTranscript && (
            <div className="bg-white border border-stone-200 rounded-xl p-3">
              <p className="text-xs text-stone-500 mb-1">You said:</p>
              <p className="text-sm font-medium text-stone-800">"{voiceTranscript}"</p>
            </div>
          )}

          {/* Voice Score Grid */}
          <div className="space-y-3">
            <div className="flex items-center justify-between">
              <div className="text-xs font-bold text-stone-500">Your Scores</div>
              {showHoleInfoCards && (
                <button
                  onClick={() => {
                    // Find first hole without a score, or current hole
                    const firstEmpty = scores.findIndex((s, i) => {
                      if (holes === 9 && startingNine === 'back') {
                        return i < 9 && !s;
                      }
                      return i < holes && !s;
                    });
                    const holeToShow = firstEmpty >= 0 
                      ? (startingNine === 'back' && holes === 9 ? firstEmpty + 10 : firstEmpty + 1)
                      : (startingNine === 'back' && holes === 9 ? 10 : 1);
                    showHoleInfo(holeToShow);
                  }}
                  className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium text-white bg-green-600 hover:bg-green-500 rounded-full transition-colors shadow-sm"
                  title="View hole information"
                  data-testid="hole-info-btn"
                >
                  <Flag className="w-3.5 h-3.5" />
                  <span>View Hole Info</span>
                </button>
              )}
            </div>
            
            {/* 9-hole mode OR Front 9 of 18-hole mode */}
            <div>
              <div className="grid grid-cols-9 gap-1">
                {scores.slice(0, Math.min(9, holes)).map((score, idx) => {
                  // For Back 9 mode with 9 holes, display holes 10-18
                  const displayHole = (holes === 9 && startingNine === 'back') ? idx + 10 : idx + 1;
                  const targetVoiceHole = displayHole;
                  const holePar = actualHolePar(holePars, idx, holes, startingNine);
                  const yardage = holeYardages[displayHole - 1];
                  return (
                    <div key={`voice-front-${idx}`} className="text-center">
                      <button 
                        onClick={(e) => {
                          e.stopPropagation();
                          if (showHoleInfoCards) showHoleInfo(displayHole);
                        }}
                        className={`text-xs mb-0.5 ${showHoleInfoCards ? 'text-green-500 font-bold hover:text-green-400 cursor-pointer' : 'text-stone-400'}`}
                        title={showHoleInfoCards ? `View Hole ${displayHole} info` : ''}
                      >
                        {displayHole}
                      </button>
                      {showYardages && yardage ? (
                        <div className="text-[8px] text-blue-400 mb-0.5">{yardage}y</div>
                      ) : null}
                      <div className="text-[9px] text-stone-300 mb-0.5">P{holePar}</div>
                      <div 
                        onClick={() => setCurrentVoiceHole(targetVoiceHole)}
                        className={`w-full h-10 flex items-center justify-center text-lg font-bold rounded-lg border-2 cursor-pointer ${
                          currentVoiceHole === targetVoiceHole 
                            ? 'border-amber-500 bg-amber-50' 
                            : score 
                              ? 'border-green-400 bg-green-50 text-green-700' 
                              : 'border-stone-200 bg-white text-stone-300'
                        }`}
                      >
                        {score || '-'}
                      </div>
                    </div>
                  );
                })}
              </div>
              <div className="flex justify-end mt-1">
                <span className="text-sm font-bold text-stone-600">
                  {(holes === 9 && startingNine === 'back') ? 'In' : 'Out'}: {front9 || '--'}
                </span>
              </div>
            </div>

            {/* Back 9 - only if 18 holes */}
            {holes === 18 && (
              <div>
                <div className="grid grid-cols-9 gap-1">
                  {scores.slice(9, 18).map((score, idx) => {
                    const displayHole = idx + 10;
                    const holePar = actualHolePar(holePars, idx + 9, holes, startingNine);
                    const yardage = holeYardages[displayHole - 1];
                    return (
                      <div key={`voice-back-${idx}`} className="text-center">
                        <button 
                          onClick={(e) => {
                            e.stopPropagation();
                            if (showHoleInfoCards) showHoleInfo(displayHole);
                          }}
                          className={`text-xs mb-0.5 ${showHoleInfoCards ? 'text-green-500 font-bold hover:text-green-400 cursor-pointer' : 'text-stone-400'}`}
                          title={showHoleInfoCards ? `View Hole ${displayHole} info` : ''}
                        >
                          {displayHole}
                        </button>
                        {showYardages && yardage ? (
                          <div className="text-[8px] text-blue-400 mb-0.5">{yardage}y</div>
                        ) : null}
                        <div className="text-[9px] text-stone-300 mb-0.5">P{holePar}</div>
                        <div 
                          onClick={() => setCurrentVoiceHole(displayHole)}
                          className={`w-full h-10 flex items-center justify-center text-lg font-bold rounded-lg border-2 cursor-pointer ${
                            currentVoiceHole === displayHole 
                              ? 'border-amber-500 bg-amber-50' 
                              : score 
                                ? 'border-green-400 bg-green-50 text-green-700' 
                                : 'border-stone-200 bg-white text-stone-300'
                          }`}
                        >
                          {score || '-'}
                        </div>
                      </div>
                    );
                  })}
                </div>
                <div className="flex justify-end mt-1">
                  <span className="text-sm font-bold text-stone-600">In: {back9 || '--'}</span>
                </div>
              </div>
            )}
          </div>

          {/* Voice Mode Total Display */}
          {showTotalDisplay && (
            <div className="bg-stone-50 rounded-xl p-4 flex items-center justify-between">
              <div>
                <p className="text-sm text-stone-500">Total</p>
                <p className="text-3xl font-black text-green-700">{total || '--'}</p>
              </div>
              <div className="text-right text-sm text-stone-500">
                {filledCount}/{holes} holes
              </div>
            </div>
          )}

          {/* Voice Mode Action Buttons */}
          <div className="flex gap-3 pt-3">
            {onCancel && (
              <Button 
                variant="outline" 
                onClick={onCancel} 
                disabled={disabled} 
                className="flex-1 h-12 text-lg font-bold"
                data-testid="voice-cancel-btn"
              >
                Close
              </Button>
            )}
            <Button 
              onClick={handleSubmit}
              disabled={disabled || saveDisabled || !allFilled}
              className="flex-1 h-12 text-lg font-bold bg-green-600 hover:bg-green-700 text-white"
              data-testid="voice-finalize-btn"
            >
              <Check className="w-5 h-5 mr-2" />
              {submitButtonText}
            </Button>
          </div>
        </div>
      )}

      {/* Hole-by-hole Score Grid - show for manual mode OR after successful scan */}
      {(entryMode === 'manual' || (entryMode === 'scan' && scanResult && scanResult.success)) && (
        <>
          {holes === 9 ? (
            /* 9 Holes Layout with Clean Score Boxes */
            <div className="space-y-2">
              <div className="flex items-center justify-between">
                <p className={`text-base font-bold ${darkMode ? 'text-gray-300' : 'text-stone-600'}`}>
                  {startingNine === 'back' ? 'Holes 10-18' : 'Holes 1-9'}
                </p>
                <p className={`text-xl font-black ${darkMode ? 'text-amber-400' : 'text-amber-600'}`}>{front9 || '--'}</p>
              </div>
              <div className="grid grid-cols-9 gap-1.5">
                {[0, 1, 2, 3, 4, 5, 6, 7, 8].map((idx) => {
                  const displayHole = getActualHoleNumber ? getActualHoleNumber(idx) : (startingNine === 'back' ? idx + 10 : idx + 1);
                  // Get par for this hole (holePars uses 0-index for hole 1)
                  const holePar = actualHolePar(holePars, idx, holes, startingNine);
                  const yardage = holeYardages[displayHole - 1];
                  return (
                    <div key={`hole-${idx}`} className="text-center">
                      <div className={`text-xs mb-0.5 font-bold ${darkMode ? 'text-gray-400' : 'text-stone-500'}`}>{displayHole}</div>
                      {showYardages && yardage ? (
                        <div className={`text-[8px] mb-0.5 ${darkMode ? 'text-blue-400' : 'text-blue-500'}`}>{yardage}y</div>
                      ) : null}
                      <div data-testid={`hole-par-${displayHole}`} className={`text-[10px] mb-1 ${darkMode ? 'text-gray-500' : 'text-stone-400'}`}>P{holePar ?? '—'}</div>
                      <ScoreBox
                        value={scores[idx] || ''}
                        onChange={(val) => handleScoreChange(idx, val)}
                        disabled={disabled || Boolean(reactionSequence.reaction)}
                        holeNumber={displayHole}
                        par={holePar}
                      />
                    </div>
                  );
                })}
              </div>
            </div>
          ) : (
            /* 18 Holes Layout - Front 9 + Back 9 with Clean Score Boxes */
            <>
              {/* Front 9 */}
              <div className="space-y-2">
                <div className="flex items-center justify-between">
                  <p className={`text-base font-bold ${darkMode ? 'text-gray-300' : 'text-stone-600'}`}>Front 9</p>
                  <p className={`text-xl font-black ${darkMode ? 'text-amber-400' : 'text-amber-600'}`}>{front9 || '--'}</p>
                </div>
                <div className="grid grid-cols-9 gap-1.5">
                  {[1, 2, 3, 4, 5, 6, 7, 8, 9].map((hole) => {
                    const holePar = actualHolePar(holePars, hole - 1, holes, startingNine);
                    const yardage = holeYardages[hole - 1];
                    return (
                      <div key={`hole-${hole}`} className="text-center">
                        <div className={`text-xs mb-0.5 font-bold ${darkMode ? 'text-gray-400' : 'text-stone-500'}`}>{hole}</div>
                        {showYardages && yardage ? (
                          <div className={`text-[8px] mb-0.5 ${darkMode ? 'text-blue-400' : 'text-blue-500'}`}>{yardage}y</div>
                        ) : null}
                        <div data-testid={`hole-par-${hole}`} className={`text-[10px] mb-1 ${darkMode ? 'text-gray-500' : 'text-stone-400'}`}>P{holePar ?? '—'}</div>
                        <ScoreBox
                          value={scores[hole - 1] || ''}
                          onChange={(val) => handleScoreChange(hole - 1, val)}
                          disabled={disabled || Boolean(reactionSequence.reaction)}
                          holeNumber={hole}
                          par={holePar}
                        />
                      </div>
                    );
                  })}
                </div>
              </div>

              {/* Back 9 */}
              <div className="space-y-2 mt-3">
                <div className="flex items-center justify-between">
                  <p className={`text-base font-bold ${darkMode ? 'text-gray-300' : 'text-stone-600'}`}>Back 9</p>
                  <p className={`text-xl font-black ${darkMode ? 'text-amber-400' : 'text-amber-600'}`}>{back9 || '--'}</p>
                </div>
                <div className="grid grid-cols-9 gap-1.5">
                  {[10, 11, 12, 13, 14, 15, 16, 17, 18].map((hole) => {
                    const holePar = actualHolePar(holePars, hole - 1, holes, startingNine);
                    const yardage = holeYardages[hole - 1];
                    return (
                      <div key={`hole-${hole}`} className="text-center">
                        <div className={`text-xs mb-0.5 font-bold ${darkMode ? 'text-gray-400' : 'text-stone-500'}`}>{hole}</div>
                        {showYardages && yardage ? (
                          <div className={`text-[8px] mb-0.5 ${darkMode ? 'text-blue-400' : 'text-blue-500'}`}>{yardage}y</div>
                        ) : null}
                        <div data-testid={`hole-par-${hole}`} className={`text-[10px] mb-1 ${darkMode ? 'text-gray-500' : 'text-stone-400'}`}>P{holePar ?? '—'}</div>
                        <ScoreBox
                          value={scores[hole - 1] || ''}
                          onChange={(val) => handleScoreChange(hole - 1, val)}
                          disabled={disabled || Boolean(reactionSequence.reaction)}
                          holeNumber={hole}
                          par={holePar}
                        />
                      </div>
                    );
                  })}
                </div>
              </div>
            </>
          )}

          {/* Total and Status Summary */}
          {showTotalDisplay && (
            <div className={`space-y-2 pt-3 ${darkMode ? 'border-t border-gray-700' : 'border-t border-stone-200'}`}>
              <div className="flex items-center justify-between">
                <div>
                  <span className={`text-lg font-semibold ${darkMode ? 'text-gray-400' : 'text-stone-600'}`}>Total: </span>
                  <span className={`text-4xl font-black ${darkMode ? 'text-amber-400' : 'text-amber-600'}`}>
                    {total || '-'}
                  </span>
                </div>
                <div className="text-right">
                  <span className={`text-base font-semibold ${darkMode ? 'text-gray-500' : 'text-stone-500'}`}>
                    {filledCount}/{holes} holes
                  </span>
                </div>
              </div>
            </div>
          )}

          {/* Action Buttons */}
          <div className="flex gap-3 pt-3">
            {onCancel && (
              <Button 
                variant="outline" 
                onClick={onCancel} 
                disabled={disabled} 
                className={`flex-1 h-12 text-lg font-bold ${darkMode ? 'border-gray-600 text-gray-300 hover:bg-gray-800' : ''}`}
                data-testid="cancel-score-btn"
              >
                Close
              </Button>
            )}
            <Button 
              onClick={handleSubmit}
              disabled={disabled || saveDisabled || !allFilled}
              className="flex-1 h-12 text-lg font-bold text-white"
              style={darkMode ? {
                background: 'linear-gradient(145deg, #22c55e 0%, #16a34a 100%)',
                boxShadow: '0 4px 12px rgba(34,197,94,0.3), inset 0 1px 0 rgba(255,255,255,0.2)'
              } : {}}
              data-testid="finalize-score-btn"
            >
              <Check className="w-5 h-5 mr-2" />
              {submitButtonText}
            </Button>
          </div>
        </>
      )}
    </div>
  );
};

export default ScoreEntryForm;
export { ScoreBox };
